import React, { useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { collectionItems } from '../data/mockData';

gsap.registerPlugin(ScrollTrigger);

const CATEGORY_FILTERS = ['Todos', 'Comedores', 'Sillas & Bancas', 'Sala', 'Recámaras', 'Exterior', 'Consolas & TV', 'Oficina', 'Equipales', 'Accesorios'];
const MATERIAL_TAGS = ['Parota', 'Rosa Morada', 'Teca', 'Madera Sólida', 'Tejido', 'Encino', 'Cuero'];

const WA_BASE = 'https://wa.me/523338283729';

function buildWALink(productName) {
  const text = encodeURIComponent(`Hola, me interesa cotizar: ${productName}`);
  return `${WA_BASE}?text=${text}`;
}

export default function Coleccion() {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [activeTag, setActiveTag] = useState(null);
  const heroRef = useRef(null);
  const gridRef = useRef(null);

  const filtered = useMemo(() => collectionItems.filter((item) => {
    const categoryMatch =
      activeCategory === 'Todos' || item.category === activeCategory;
    const tagMatch =
      !activeTag ||
      (item.material && item.material.toLowerCase().includes(activeTag.toLowerCase()));
    return categoryMatch && tagMatch;
  }), [activeCategory, activeTag]);

  // Hero entrance
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.col-hero-title',
        { opacity: 0, y: 36 },
        { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', delay: 0.1 }
      );
      gsap.fromTo(
        '.col-hero-sub',
        { opacity: 0, y: 22 },
        { opacity: 1, y: 0, duration: 0.75, ease: 'power3.out', delay: 0.3 }
      );
    }, heroRef);
    return () => ctx.revert();
  }, []);

  // Product cards scroll reveal — re-run on filter change
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    ScrollTrigger.getAll()
      .filter((t) => t.vars?.id === 'product-reveal')
      .forEach((t) => t.kill());

    const ctx = gsap.context(() => {
      // Set initial state immediately so cards don't flash
      gsap.set('.product-card', { opacity: 0, y: 28 });
      gsap.to('.product-card', {
        opacity: 1,
        y: 0,
        stagger: 0.07,
        duration: 0.55,
        ease: 'power3.out',
        scrollTrigger: {
          id: 'product-reveal',
          trigger: '.product-grid',
          start: 'top 88%',
        },
      });
    }, gridRef);
    return () => ctx.revert();
  }, [activeCategory, activeTag]);

  return (
    <>
      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative flex items-center justify-center overflow-hidden"
        style={{ minHeight: '50vh' }}
      >
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1800&q=85"
            alt="Catálogo Del Sol Muebles"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-wood/65 via-wood/50 to-wood/75" />
        </div>

        <div className="relative z-10 text-center px-6 py-20">
          <p className="col-hero-sub font-sans text-[10px] tracking-[0.3em] uppercase text-gold mb-5 opacity-0">
            Del Sol Muebles · Tonalá, Jalisco
          </p>
          <h1
            className="col-hero-title font-serif text-display-sm md:text-display text-white mb-6 opacity-0"
            style={{ letterSpacing: '0.05em' }}
          >
            Catálogo Completo
          </h1>
          <p className="col-hero-sub font-sans text-sm md:text-base text-cream/75 max-w-xl mx-auto tracking-wide opacity-0">
            Muebles a la medida · Todos los precios son por cotización personalizada
          </p>
        </div>
      </section>

      {/* ── Catalog Section ──────────────────────────────────────────── */}
      <section className="bg-cream-light py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 justify-start md:justify-center mb-5">
            {CATEGORY_FILTERS.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`font-sans text-[10px] tracking-[0.18em] uppercase px-4 py-2 rounded-sm border transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-terracotta border-terracotta text-white'
                    : 'bg-transparent border-wood/30 text-wood hover:border-terracotta hover:text-terracotta'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Material Tag Chips */}
          <div className="flex flex-wrap gap-2 justify-center mb-14">
            {MATERIAL_TAGS.map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                className={`font-sans text-[9px] tracking-[0.18em] uppercase px-3.5 py-1 rounded-full border transition-all duration-200 ${
                  activeTag === tag
                    ? 'bg-gold border-gold text-wood font-semibold'
                    : 'bg-transparent border-gold/40 text-taupe hover:border-gold hover:text-wood'
                }`}
              >
                {tag}
              </button>
            ))}
            {activeTag && (
              <button
                onClick={() => setActiveTag(null)}
                className="font-sans text-[9px] tracking-[0.15em] uppercase px-3.5 py-1 rounded-full border border-taupe/30 text-taupe hover:border-terracotta hover:text-terracotta transition-all duration-200"
              >
                Limpiar ×
              </button>
            )}
          </div>

          {/* Product Count */}
          <p className="font-sans text-xs text-taupe tracking-widest uppercase text-center mb-10">
            Mostrando{' '}
            <span className="text-wood font-semibold">{filtered.length}</span> de{' '}
            <span className="text-wood font-semibold">{collectionItems.length}</span> muebles
          </p>

          {/* Product Grid */}
          <div ref={gridRef}>
            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <p className="font-serif text-2xl text-wood mb-3">
                  No hay productos en esta categoría.
                </p>
                <button
                  onClick={() => {
                    setActiveCategory('Todos');
                    setActiveTag(null);
                  }}
                  className="font-sans text-sm text-terracotta underline underline-offset-4"
                >
                  Ver todos →
                </button>
              </div>
            ) : (
              <div className="product-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {filtered.map((item) => (
                  <article key={item.id} className="product-card group" style={{ opacity: 0 }}>
                    {/* Image */}
                    <div className="aspect-[4/5] overflow-hidden rounded-sm shadow-md mb-5 relative">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      />
                      {/* Category badge on image */}
                      <span className="absolute top-3 left-3 font-sans text-[9px] tracking-[0.2em] uppercase bg-wood/80 text-cream px-2.5 py-1 rounded-sm">
                        {item.category}
                      </span>
                    </div>

                    {/* Tag */}
                    <span className="font-sans text-[9px] tracking-[0.22em] uppercase text-gold font-medium mb-1.5 block">
                      {item.material}
                    </span>

                    {/* Name */}
                    <h3 className="font-serif text-xl md:text-2xl text-wood group-hover:text-terracotta transition-colors duration-300 mb-1.5 leading-snug">
                      {item.name}
                    </h3>

                    {/* Dimensions */}
                    {item.dimensions && (
                      <p className="font-sans text-[10px] text-taupe/60 mb-4">
                        {item.dimensions}
                      </p>
                    )}

                    {/* No price — cotización only */}
                    <p className="font-sans text-[10px] tracking-[0.15em] uppercase text-taupe/70 mb-4">
                      Cotización personalizada
                    </p>

                    {/* CTA */}
                    <a
                      href={buildWALink(item.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center font-sans text-[10px] tracking-[0.18em] uppercase border border-terracotta text-terracotta hover:bg-terracotta hover:text-white py-3 rounded-sm transition-all duration-300"
                    >
                      Cotizar este Mueble
                    </a>
                  </article>
                ))}
              </div>
            )}
          </div>

          {/* Pricing note */}
          <div className="mt-20 max-w-2xl mx-auto text-center">
            <div className="w-10 h-px bg-gold mx-auto mb-8" />
            <p className="font-sans text-sm text-taupe leading-relaxed">
              Todos nuestros muebles son a la medida. El precio depende de la madera, tamaño y
              complejidad de tu proyecto.
            </p>
          </div>

          {/* WhatsApp CTA Block */}
          <div className="mt-16 bg-wood rounded-sm px-8 py-14 text-center max-w-3xl mx-auto shadow-lg">
            <span className="font-sans text-[9px] tracking-[0.3em] uppercase text-gold/70 mb-4 block">
              ¿Listo para crear tu mueble?
            </span>
            <h3 className="font-serif text-3xl md:text-4xl text-white mb-5">
              Hablemos de tu proyecto
            </h3>
            <p className="font-sans text-sm text-cream/70 mb-10 leading-relaxed max-w-lg mx-auto">
              Escríbenos por WhatsApp y recibe una cotización personalizada sin compromiso.
              Atendemos de lunes a sábado.
            </p>
            <a
              href={WA_BASE}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-sans font-semibold text-sm tracking-wide px-8 py-4 rounded-sm transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Iniciar Cotización por WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
